import { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import { roomsAPI, bedDispositionsAPI } from '../../services/api';
import { SUCCESS_MESSAGES } from '../../utils/constants';
import { FiPlus, FiSearch, FiGrid, FiList, FiSettings, FiMapPin, FiKey } from 'react-icons/fi';
import { Skeleton, SkeletonCard, SkeletonTable } from '../../design-system';
import RoomForm from '../../components/rooms/RoomForm';
import RoomGrid from '../../components/rooms/RoomGrid';
import RoomTable from '../../components/rooms/RoomTable';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import AdditionalSettingsTab from '../../components/rooms/AdditionalSettingsTab';
import CommonSpacesTab from '../../components/rooms/CommonSpacesTab';

const RoomManagement = () => {
  const { isMobile, addNotification } = useApp();
  const [rooms, setRooms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showRoomForm, setShowRoomForm] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilters, setStatusFilters] = useState([]); // Array para múltiples estados
  const [dispositionFilters, setDispositionFilters] = useState([]); // Array para disposiciones de camas
  const [bedDispositions, setBedDispositions] = useState([]);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'table'
  const [activeTab, setActiveTab] = useState('rooms'); // 'rooms' or 'settings'

  useEffect(() => {
    loadRooms();
    loadBedDispositions();
  }, []);

  const loadRooms = async () => {
    try {
      setIsLoading(true);
      const roomsData = await roomsAPI.getAll();
      setRooms(roomsData);
    } catch (error) {
      console.error('Error loading rooms:', error);
      addNotification({
        type: 'error',
        message: 'Error al cargar las habitaciones'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadBedDispositions = async () => {
    try {
      const dispositions = await bedDispositionsAPI.getAll();
      setBedDispositions(dispositions);
    } catch (error) {
      console.error('Error loading bed dispositions:', error);
    }
  };

  const handleCreateRoom = () => {
    setSelectedRoom(null);
    setShowRoomForm(true);
  };

  const handleEditRoom = (room) => {
    setSelectedRoom(room);
    setShowRoomForm(true);
  };

  const handleDeleteRoom = (room) => {
    setDeleteConfirm(room);
  };

  const confirmDeleteRoom = async () => {
    try {
      await roomsAPI.delete(deleteConfirm.id);
      setRooms(prevRooms => prevRooms.filter(r => r.id !== deleteConfirm.id));
      setDeleteConfirm(null);

      addNotification({
        type: 'success',
        message: 'Habitación eliminada exitosamente'
      });
    } catch (error) {
      console.error('Error deleting room:', error);
      addNotification({
        type: 'error',
        message: 'Error al eliminar la habitación'
      });
    }
  };

  const handleRoomSaved = async (roomData) => {
    try {
      if (selectedRoom) {
        // Update existing room
        const updatedRoom = await roomsAPI.update(selectedRoom.id, roomData);
        setRooms(prevRooms =>
          prevRooms.map(r => (r.id === selectedRoom.id ? updatedRoom : r))
        );
        addNotification({
          type: 'success',
          message: SUCCESS_MESSAGES.ROOM_UPDATED
        });
      } else {
        // Create new room
        const newRoom = await roomsAPI.create(roomData);
        setRooms(prevRooms => [...prevRooms, newRoom]);
        addNotification({
          type: 'success',
          message: SUCCESS_MESSAGES.ROOM_CREATED
        });
      }

      setShowRoomForm(false);
      setSelectedRoom(null);
    } catch (error) {
      console.error('Error saving room:', error);
      addNotification({
        type: 'error',
        message: 'Error al guardar la habitación'
      });
    }
  };


  const getFilteredRooms = () => {
    return rooms.filter(room => {
      const matchesSearch = !searchTerm ||
        room.number.toLowerCase().includes(searchTerm.toLowerCase());

      // Filtro por estado calculado - si no hay filtros seleccionados, mostrar todos
      let matchesStatus = true;
      if (statusFilters.length > 0) {
        matchesStatus = statusFilters.some(filter => {
          if (filter === 'incident') {
            return room.has_incidents_today;
          } else if (filter === 'clean') {
            return room.current_status === 'clean' && !room.has_incidents_today;
          } else if (filter === 'dirty') {
            return room.current_status === 'dirty' && !room.has_incidents_today;
          } else if (filter === 'maintenance') {
            return room.current_status === 'maintenance' && !room.has_incidents_today;
          }
          return false;
        });
      }

      // Filtro por disposiciones de camas - si no hay filtros seleccionados, mostrar todos
      let matchesDisposition = true;
      if (dispositionFilters.length > 0) {
        matchesDisposition = room.bed_dispositions && room.bed_dispositions.some(
          disposition => dispositionFilters.includes(disposition.id)
        );
      }

      return matchesSearch && matchesStatus && matchesDisposition;
    }).sort((a, b) => {
      // Ordenar por número de habitación
      const numA = parseInt(a.number) || 0;
      const numB = parseInt(b.number) || 0;
      return numA - numB;
    });
  };

  const toggleStatusFilter = (status) => {
    setStatusFilters(prev =>
      prev.includes(status)
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };

  const toggleDispositionFilter = (dispositionId) => {
    setDispositionFilters(prev =>
      prev.includes(dispositionId)
        ? prev.filter(id => id !== dispositionId)
        : [...prev, dispositionId]
    );
  };

  const getRoomStats = () => {
    const total = rooms.length;
    const clean = rooms.filter(r => r.current_status === 'clean' && !r.has_incidents_today).length;
    const dirty = rooms.filter(r => r.current_status === 'dirty' && !r.has_incidents_today).length;
    const incidents = rooms.filter(r => r.has_incidents_today).length;

    return { total, clean, dirty, incidents };
  };

  const filteredRooms = getFilteredRooms();
  const stats = getRoomStats();

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
          Configuración
        </h1>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-4 sm:space-x-8 overflow-x-auto">
          <button
            onClick={() => setActiveTab('rooms')}
            className={`py-2 px-1 border-b-2 font-medium text-xs sm:text-sm flex items-center space-x-1 sm:space-x-2 whitespace-nowrap ${
              activeTab === 'rooms'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FiKey className="h-3 w-3 sm:h-4 sm:w-4" />
            <span>Habitaciones</span>
          </button>
          <button
            onClick={() => setActiveTab('common-spaces')}
            className={`py-2 px-1 border-b-2 font-medium text-xs sm:text-sm flex items-center space-x-1 sm:space-x-2 whitespace-nowrap ${
              activeTab === 'common-spaces'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FiMapPin className="h-3 w-3 sm:h-4 sm:w-4" />
            <span>Espacios Comunes</span>
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`py-2 px-1 border-b-2 font-medium text-xs sm:text-sm flex items-center space-x-1 sm:space-x-2 whitespace-nowrap ${
              activeTab === 'settings'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FiSettings className="h-3 w-3 sm:h-4 sm:w-4" />
            <span>Configuraciones</span>
          </button>
        </nav>
      </div>

      {activeTab === 'rooms' && (
        <>
      {/* Stats */}
      {isLoading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="bg-white rounded-lg shadow p-3 sm:p-4">
              <div className="flex items-center gap-3">
                <Skeleton variant="circle" width="48px" height="48px" />
                <div className="flex-1 space-y-2">
                  <Skeleton variant="text" width="50%" height="14px" />
                  <Skeleton variant="text" width="40%" height="24px" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <div className="bg-white rounded-lg shadow p-3 sm:p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0 rounded-md p-2 sm:p-3 bg-blue-100">
                <FiGrid className="h-4 w-4 sm:h-6 sm:w-6 text-blue-600" />
              </div>
              <div className="ml-2 sm:ml-4 min-w-0">
                <p className="text-xs sm:text-sm font-medium text-gray-600">Total</p>
                <p className="text-lg sm:text-2xl font-semibold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-3 sm:p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0 rounded-md p-2 sm:p-3 bg-green-100">
                <span className="text-green-600 font-bold text-sm sm:text-base">✓</span>
              </div>
              <div className="ml-2 sm:ml-4 min-w-0">
                <p className="text-xs sm:text-sm font-medium text-gray-600">Limpias</p>
                <p className="text-lg sm:text-2xl font-semibold text-gray-900">{stats.clean}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-3 sm:p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0 rounded-md p-2 sm:p-3 bg-red-100">
                <span className="text-red-600 font-bold text-sm sm:text-base">✗</span>
              </div>
              <div className="ml-2 sm:ml-4 min-w-0">
                <p className="text-xs sm:text-sm font-medium text-gray-600">Sucias</p>
                <p className="text-lg sm:text-2xl font-semibold text-gray-900">{stats.dirty}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-3 sm:p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0 rounded-md p-2 sm:p-3 bg-orange-100">
                <span className="text-orange-600 font-bold text-sm sm:text-base">⚠</span>
              </div>
              <div className="ml-2 sm:ml-4 min-w-0">
                <p className="text-xs sm:text-sm font-medium text-gray-600">Incidencias</p>
                <p className="text-lg sm:text-2xl font-semibold text-gray-900">{stats.incidents}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-3 sm:p-4">
        <div className="space-y-3">
          {/* Primera fila: Búsqueda y botón crear */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Buscar habitación..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input pl-9 w-full text-sm"
              />
            </div>

            {/* Nueva habitación button */}
            <button
              onClick={handleCreateRoom}
              className="btn btn-primary flex items-center justify-center space-x-2 whitespace-nowrap"
            >
              <FiPlus className="h-4 w-4" />
              <span>Nueva Habitación</span>
            </button>
          </div>

          {/* Segunda fila: Filtros */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            {/* Status filter buttons */}
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => toggleStatusFilter('clean')}
                className={`px-3 py-2 text-xs font-semibold rounded-full border transition-colors ${
                  statusFilters.includes('clean')
                    ? 'bg-green-100 text-green-800 border-green-200'
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Limpia
              </button>
              <button
                onClick={() => toggleStatusFilter('dirty')}
                className={`px-3 py-2 text-xs font-semibold rounded-full border transition-colors ${
                  statusFilters.includes('dirty')
                    ? 'bg-red-100 text-red-800 border-red-200'
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Pendiente
              </button>
              <button
                onClick={() => toggleStatusFilter('incident')}
                className={`px-3 py-2 text-xs font-semibold rounded-full border transition-colors ${
                  statusFilters.includes('incident')
                    ? 'bg-orange-100 text-orange-800 border-orange-200'
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Incidencia
              </button>
            </div>

            {/* Disposition filter buttons */}
            {bedDispositions.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 sm:pl-3 sm:border-l sm:border-gray-300">
                {bedDispositions.map(disposition => (
                  <button
                    key={disposition.id}
                    onClick={() => toggleDispositionFilter(disposition.id)}
                    className={`px-3 py-2 text-xs font-semibold rounded-full border transition-colors ${
                      dispositionFilters.includes(disposition.id)
                        ? 'bg-blue-100 text-blue-800 border-blue-200'
                        : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {disposition.name}
                  </button>
                ))}
              </div>
            )}

            {/* View mode toggle - Solo desktop */}
            {!isMobile && (
              <div className="flex rounded-md shadow-sm ml-auto">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`px-3 py-2 text-sm font-medium rounded-l-md border ${
                    viewMode === 'grid'
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <FiGrid className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setViewMode('table')}
                  className={`px-3 py-2 text-sm font-medium rounded-r-md border-t border-r border-b ${
                    viewMode === 'table'
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <FiList className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Rooms List */}
      {isLoading ? (
        viewMode === 'grid' || isMobile ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : (
          <SkeletonTable rows={10} columns={4} showActions />
        )
      ) : (
        viewMode === 'grid' || isMobile ? (
          <RoomGrid
            rooms={filteredRooms}
            onEdit={handleEditRoom}
            onDelete={handleDeleteRoom}
          />
        ) : (
          <RoomTable
            rooms={filteredRooms}
            onEdit={handleEditRoom}
            onDelete={handleDeleteRoom}
          />
        )
      )}
        </>
      )}

      {/* Common Spaces Tab */}
      {activeTab === 'common-spaces' && (
        <CommonSpacesTab />
      )}

      {/* Additional Settings Tab */}
      {activeTab === 'settings' && (
        <AdditionalSettingsTab />
      )}

      {/* Room Form Modal */}
      {showRoomForm && (
        <RoomForm
          room={selectedRoom}
          existingRooms={rooms}
          onSave={handleRoomSaved}
          onClose={() => {
            setShowRoomForm(false);
            setSelectedRoom(null);
          }}
        />
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <ConfirmDialog
          title="Eliminar Habitación"
          message={`¿Estás seguro de que deseas eliminar la habitación "${deleteConfirm.number}"? Esta acción no se puede deshacer.`}
          confirmText="Eliminar"
          confirmStyle="danger"
          onConfirm={confirmDeleteRoom}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}
    </div>
  );
};

export default RoomManagement;